import { useCallback, useEffect, useRef, useState } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { JSONNode } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { isAIFCAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { trackRemixCreateOptionSelected } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type {
	StreamActionClassification,
	StreamActionPart,
	StreamActionPrepare,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	isStoreError,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type {
	StoreAssistant,
	StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useConversationById } from '@atlassian/conversation-assistant/controllers/chat-store';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

import { isRemixRestrictedNodeType } from '../remix/remix-config';
import {
	getSubTypeDisplayName,
	getTypeDisplayName,
	type RemixOptionSelectionParameters,
} from '../remix/remix-overlay';

import { useEditorRemixContext } from './context';
import { getUnsupportedRemixNodeTypes } from './getUnsupportedRemixNodeTypes';
import type { DynamicUiSource, RemixProductConfig, RemixProductIntegration } from './types';
import {
	getContentClassificationFromActionClassification,
	getFinalAIFCMessage,
	withWhiteboardCreateActionData,
} from './utils/message';
import { getFinalMessageId } from './utils/remix-ids';

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 */
/**
 * Extracts frame titles from a multiBodiedExtension's parameters.
 *
 * Searches for any array property in `parameters` whose length matches the
 * frame count and whose items have a `title` field. This avoids hard-coding
 * macro-specific parameter names (e.g. `tabs`) so it scales to any MBE
 * variant that stores per-frame titles in its parameters.
 */
const extractFrameTitles = (
	node: JSONNode,
	frameCount: number,
): Array<string | undefined> | undefined => {
	const attrs = node.attrs as Record<string, unknown> | undefined;
	const parameters = attrs?.parameters as Record<string, unknown> | undefined;
	if (!parameters) {
		return undefined;
	}
	for (const value of Object.values(parameters)) {
		if (
			Array.isArray(value) &&
			value.length === frameCount &&
			value.some((item) => typeof item === 'object' && item !== null && 'title' in item)
		) {
			return value.map((item: { title?: string }) => item.title);
		}
	}
	return undefined;
};

/**
 * Unwraps a multiBodiedExtension node, extracting inner content from each
 * extensionFrame so the AI receives clean ADF (paragraphs, lists, etc.)
 * instead of extension wrapper types it cannot process.
 *
 * When frame titles are available in the node's parameters, a heading is
 * injected before each frame's content so the AI preserves the logical
 * grouping rather than treating everything as one flat list.
 */
const unwrapMultiBodiedExtension = (node: JSONNode, result: JSONNode[]): void => {
	const frames = (node.content ?? []).filter(
		(frame): frame is JSONNode => frame?.type === 'extensionFrame' && Array.isArray(frame?.content),
	);
	const frameTitles = extractFrameTitles(node, frames.length);

	frames.forEach((frame, index) => {
		const title = frameTitles?.[index];
		if (title) {
			result.push({
				type: 'heading',
				attrs: { level: 2 },
				content: [{ type: 'text', text: title }],
			});
		}

		for (const child of frame.content!) {
			const filtered = traverse(child as ADFEntity, {
				any: (adfNode) => {
					if (isRemixRestrictedNodeType(adfNode.type)) {
						return false;
					}
				},
			});
			if (filtered !== false) {
				result.push(filtered as JSONNode);
			}
		}
	});
};

export const filterRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		if (node.type === 'multiBodiedExtension' && Array.isArray(node.content)) {
			unwrapMultiBodiedExtension(node, result);
			continue;
		}

		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isRemixRestrictedNodeType(adfNode.type)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

const isActionCompleteNew = (chatMessages: StoreChatItem[]): boolean => {
	// Scan backwards to find ACTION_COMPLETE in the latest turn.
	// If we encounter a HUMAN message first, that marks a turn boundary —
	// any ACTION_COMPLETE before it belongs to a previous turn.
	let hasActionCompleteStep = false;
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];

		if (message.type === 'ACTION_COMPLETE') {
			hasActionCompleteStep = true;
			break;
		}

		if (isStoreHuman(message)) {
			// Stop searching when we reach the end of the most recent turn.
			break;
		}
	}

	const lastPlannedAIFCAction = findLastAssistantAction(
		chatMessages,
		(action) => isAIFCAction(action) && action.status === 'planned',
	);
	const hasCompletedAction = lastPlannedAIFCAction !== undefined;

	return hasActionCompleteStep || hasCompletedAction;
};

const isActionComplete = (chatMessages: StoreChatItem[]): boolean => {
	if (fg('remix_product_modal_rovo_chat')) {
		return isActionCompleteNew(chatMessages);
	}

	const hasWhiteboard =
		findLastAssistantAction(
			chatMessages,
			(action) =>
				action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
				action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
		) !== undefined;
	if (hasWhiteboard) {
		return true;
	}
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_COMPLETE') {
			return true;
		}
		if (message.type === 'HUMAN') {
			return false;
		}
	}
	return false;
};

const isWhiteboardCreateOrUpdateKey = (key: string): boolean =>
	key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY || key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY;

const shouldUseEphemeralPreviewForAction = (action: { key: string; data?: unknown }): boolean =>
	isRemixGeneratedFlow(action);

// When free-text remix produces a whiteboard (isRemixGenerated=true, no dynamicUiSubtype),
// open the full AIFC staging modal rather than inserting a URL into the page.
const shouldUseAifcStagingAreaForAction = (action: { key: string; data?: unknown }): boolean => {
	if (!isRemixGeneratedFlow(action)) {
		return false;
	}
	return (
		isWhiteboardCreateOrUpdateKey(action.key) &&
		!(action.data as { dynamicUiSubtype?: string } | undefined)?.dynamicUiSubtype
	);
};

const defaultShouldUseEphemeralPreview = (chatMessages: StoreChatItem[]): boolean => {
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_PREPARE') {
			return message.action ? shouldUseEphemeralPreviewForAction(message.action) : false;
		}
	}
	return false;
};

const defaultIsActionReady = (chatMessages: StoreChatItem[]): boolean =>
	isActionComplete(chatMessages);

export const useRemixToAIFC = ({
	config,
	onActionReady,
	productIntegration,
	onStagingAIFCFlowStarted,
}: {
	config: RemixProductConfig;
	onActionReady?: () => void;
	productIntegration: RemixProductIntegration;
	/**
	 * Called when a non-remix AIFC object output is handed off to the full Smart Creation
	 * staging modal. The editor Remix UI should clear local overlay/decoration state at
	 * this point, because the rest of the create flow is owned by the staging modal rather
	 * than by the editor-owned Remix insertion flow. This must not stop/cancel thinking,
	 * because the AIFC message stream is still active and needs to continue in the modal.
	 */
	onStagingAIFCFlowStarted?: () => void;
}): {
	closeRemixPreview: () => void;
	isRemixAborted: boolean;
	isRemixError: boolean;
	isRemixSafetyViolation: boolean;
	openRemixPreview: (options?: {
		action?: { key: string; data?: unknown };
		remixConversationId?: string;
	}) => void;
	remixStoreError: StoreError | null;
	sendAIFCPrompt: ({
		userPrompt,
		dynamicUiSubtype,
		dynamicUiType,
		dynamicUiSource,
		interactionSource,
		aspectRatio,
		aiFeatureName,
		isMediaInsertFlow,
	}: {
		aspectRatio?: string | null;
		dynamicUiSource: DynamicUiSource | undefined;
		dynamicUiSubtype?: string;
		dynamicUiType: RemixOptionSelectionParameters['type'];
		interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
		userPrompt?: string;
		aiFeatureName?: string;
		isMediaInsertFlow?: boolean;
	}) => Promise<void>;
	stopRemixing: () => void;
} => {
	const {
		isUpdate,
		conversationId,
		setConversationId,
		setFinalMessage,
		setContentClassification,
		remixUpdateSentinelRef,
	} = useEditorRemixContext();
	const experienceTracker = useAIMateExperienceTracker();
	const { sendMessage, createConversationOptimistically, abortRequests, activeConversation } =
		useSmartCreationChatStoreApi();
	const {
		setPrompt,
		setDynamicUiContext,
		setUserSelectedDynamicUiSubtype,
		setForceSkipAutoConversationSelection,
	} = useStagingAreaActions();
	const { preferences } = useUserPreferences();
	// remove local state when cleanup platform_editor_ai_remix_in_editor_loading
	const [remixConversationId, setRemixConversationId] = useState<string | undefined>(undefined);

	const activeRemixConversation =
		activeConversation?.id && activeConversation.id === remixConversationId
			? activeConversation
			: null;
	const remixConversationById = useConversationById({
		conversationId: conversationId ?? '',
	});
	const remixConversation = fg('platform_editor_ai_remix_in_editor_loading')
		? remixConversationById
		: activeRemixConversation;
	const remixConversationIdToRead = fg('platform_editor_ai_remix_in_editor_loading')
		? conversationId
		: activeRemixConversation?.id;

	const [chatMessages] = useChatMessages({ conversationId: remixConversationIdToRead ?? '' });
	let finalMessage = fg('platform_editor_ai_remix_in_editor_loading')
		? getFinalAIFCMessage(chatMessages)
		: undefined;

	// If it's an update, suppress the previously-displayed result until the new
	// variant response arrives. The sentinel is the id of the final message that
	// was on the page when the update was initiated, captured live in
	// useUpdateRemix — stored remix-history ids can be stale (e.g. a BE-driven Ask
	// Rovo edit inherits the prior entry's id rather than its own message id).
	const currentFinalMessageId = getFinalMessageId(finalMessage);
	const previousFinalMessageId = remixUpdateSentinelRef.current;
	if (
		isUpdate &&
		previousFinalMessageId &&
		previousFinalMessageId === currentFinalMessageId &&
		fg('platform_editor_ai_remix_toolbar')
	) {
		finalMessage = undefined;
	}

	const remixStoreError =
		chatMessages.find((message): message is StoreError => isStoreError(message)) ?? null;
	const isRemixError =
		remixConversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ||
		remixStoreError !== null;

	const isRemixAborted =
		!isRemixError &&
		remixConversation?.status === CHAT_STORE_STATUS.idle &&
		!isActionComplete(chatMessages) &&
		chatMessages.some(isStoreAssistant);

	// Set on a (non-fatal) safety/AUP violation. It looks like a completed
	// action here, so isRemixError/isRemixAborted miss it — this flag lets the component
	// dismiss the thinking bar and open the Rovo side chat.
	const [isRemixSafetyViolation, setIsRemixSafetyViolation] = useState(false);

	const actionReadyFired = useRef(false);
	const hasOpenedFullStagingModalRef = useRef(false);
	const recordedFinalMessageId = useRef<string | undefined>(undefined);
	const recordedFinalMessage = useRef<StoreAssistant | undefined>(undefined);
	const previousConversationId = useRef(remixConversationIdToRead);

	useEffect(() => {
		if (previousConversationId.current !== remixConversationIdToRead) {
			actionReadyFired.current = false;
			hasOpenedFullStagingModalRef.current = false;
			recordedFinalMessageId.current = undefined;
			recordedFinalMessage.current = undefined;
			previousConversationId.current = remixConversationIdToRead;
			setIsRemixSafetyViolation(false);
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setFinalMessage(undefined);
			}
		}
	}, [remixConversationIdToRead, setFinalMessage]);

	useEffect(() => {
		if (actionReadyFired.current || !onActionReady) {
			return;
		}

		const isActionReady =
			config.isActionReady?.(chatMessages) ?? defaultIsActionReady(chatMessages);

		if (isActionReady) {
			actionReadyFired.current = true;
			onActionReady();
		}
	}, [config, chatMessages, onActionReady]);

	useEffect(() => {
		const finalMessageId = getFinalMessageId(finalMessage);
		const hasRecordedFinalMessage =
			finalMessageId !== undefined
				? recordedFinalMessageId.current === finalMessageId
				: recordedFinalMessage.current === finalMessage;
		if (
			finalMessage &&
			!hasRecordedFinalMessage &&
			!hasOpenedFullStagingModalRef.current &&
			fg('platform_editor_ai_remix_in_editor_loading')
		) {
			recordedFinalMessageId.current = finalMessageId;
			recordedFinalMessage.current = finalMessage;
			setFinalMessage(finalMessage);
			experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		}
	}, [finalMessage, setFinalMessage, experienceTracker]);

	const stopRemixing = useCallback(() => {
		abortRequests?.({ conversationId: remixConversationIdToRead });
	}, [abortRequests, remixConversationIdToRead]);

	// Despite the name, this opens the appropriate AIFC surface for the generated output:
	// remix-generated outputs use the floating ephemeral preview, while non-remix AIFC
	// object outputs (page, database, whiteboard, slides, etc.) use the full staging modal.
	const openRemixPreview = useCallback(
		(options?: { action?: { key: string; data?: unknown }; remixConversationId?: string }) => {
			setForceSkipAutoConversationSelection(true);
			let shouldUseEphemeralPreview: boolean;
			if (options?.action) {
				shouldUseEphemeralPreview =
					!shouldUseAifcStagingAreaForAction(options.action) &&
					shouldUseEphemeralPreviewForAction(options.action);
			} else {
				shouldUseEphemeralPreview =
					config.shouldUseEphemeralPreview?.(chatMessages) ??
					defaultShouldUseEphemeralPreview(chatMessages);
			}

			productIntegration.openRemixPreview({
				isFloating: shouldUseEphemeralPreview,
				remixConversationId: options?.remixConversationId ?? remixConversationIdToRead,
			});
		},
		[
			config,
			chatMessages,
			productIntegration,
			remixConversationIdToRead,
			setForceSkipAutoConversationSelection,
		],
	);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			dynamicUiSubtype,
			dynamicUiType,
			dynamicUiSource,
			interactionSource,
			aspectRatio,
			aiFeatureName,
			isMediaInsertFlow,
		}: {
			aspectRatio?: string | null;
			dynamicUiSource: DynamicUiSource | undefined;
			dynamicUiSubtype?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
			userPrompt?: string;
			aiFeatureName?: string;
			isMediaInsertFlow?: boolean;
		}) => {
			const defaultPromptText = 'Remix the selected content';
			const selectedDisplayName = dynamicUiSubtype
				? getSubTypeDisplayName(dynamicUiSubtype)
				: getTypeDisplayName(dynamicUiType);
			const autoText = `Remix the selected content as a ${selectedDisplayName}`;
			const promptText =
				dynamicUiType === 'custom' || (dynamicUiType === 'image' && userPrompt && isMediaInsertFlow)
					? (userPrompt ?? defaultPromptText)
					: userPrompt
						? `${autoText}. ${userPrompt}`
						: autoText;

			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId = crypto.randomUUID();
			setRemixConversationId(newConversationId);
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setConversationId(newConversationId);
				setFinalMessage(undefined);
				setContentClassification(undefined);
			}
			actionReadyFired.current = false;

			await createConversationOptimistically({ id: newConversationId });

			trackRemixCreateOptionSelected({
				experienceTracker,
				conversationId: newConversationId,
				remixType: dynamicUiType,
				remixSubtype: dynamicUiSubtype,
				includesFreegenPrompt: !!userPrompt,
			});

			// 'custom' is a free-form prompt with no block type — maps to REMIX_CUSTOM.
			// Everything else (chart/diagram/visual-aid/infographic/etc.) is REMIX with the
			// block type as `blocks`. The backend ConfluenceExperienceValidator rejects
			// REMIX without `blocks`, and rejects REMIX_CUSTOM with `blocks`.
			const isCustom = dynamicUiType === 'custom';
			const isRemixExperienceContextEnabled = expValEquals(
				'rovo_remix_experience_context',
				'isEnabled',
				true,
			);
			const creationContextParamsToSend = {
				dynamicUiType,
				dynamicUiSubtype,
				dynamicUiSource,
				source: 'remix' as const,
				...(isRemixExperienceContextEnabled && (isCustom || dynamicUiType)
					? { experience: isCustom ? ('remix_custom' as const) : ('remix' as const) }
					: {}),
				...(isRemixExperienceContextEnabled && !isCustom && dynamicUiType
					? { blocks: [dynamicUiType] }
					: {}),
				...(aspectRatio ? { additionalContext: { aspectRatio } } : {}),
				...(!isCustom && userPrompt ? { hasUserInstruction: true } : {}),
			};
			const unsupportedRemixNodeTypes = getUnsupportedRemixNodeTypes(
				dynamicUiSource,
				config.isNodeUnsupportedForRemix,
			);

			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource,
					followUpSource: interactionSource,
					aiFeatureName,
				},
				promptStreamHook: fg('platform_editor_ai_remix_in_editor_loading')
					? {
							// Flag the (non-fatal) safety/AUP violation so the component
							// can dismiss the thinking bar and open the Rovo side chat.
							onSafetyViolation: () => {
								setIsRemixSafetyViolation(true);
							},
							onActionClassification: async (actionClassification: StreamActionClassification) => {
								setContentClassification(
									getContentClassificationFromActionClassification(actionClassification),
								);
							},
							onActionPart: async (actionPart: StreamActionPart | StreamActionPrepare) => {
								if (actionPart.type === 'ACTION_PREPARE' && !hasOpenedFullStagingModalRef.current) {
									const useAifcStagingArea = shouldUseAifcStagingAreaForAction(actionPart.message);
									const useEphemeralPreview =
										!useAifcStagingArea && shouldUseEphemeralPreviewForAction(actionPart.message);
									if (!useEphemeralPreview) {
										hasOpenedFullStagingModalRef.current = true;
										openRemixPreview({
											action: actionPart.message,
											remixConversationId: newConversationId,
										});
										onStagingAIFCFlowStarted?.();
									}
								}

								// ACTION_CLASSIFICATION is the semantic source of truth for the stream's
								// content type. ACTION_PREPARE may carry a contentId, but it does not yet
								// represent visible whiteboard output. Wait for the first real ACTION_PART
								// before inserting the whiteboard so users do not see an empty embed.
								if (
									actionPart.type === 'ACTION_PART' &&
									actionPart.message.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY &&
									!hasOpenedFullStagingModalRef.current
								) {
									setContentClassification((classification) =>
										withWhiteboardCreateActionData(classification, actionPart.message.data),
									);
								}
							},
						}
					: undefined,
				productKey: config.productKey,
				contentContext: 'staging-area',
				unsupportedRemixNodeTypes,
				creationContextParams: creationContextParamsToSend,
			});

			setPrompt('Remix');
			setDynamicUiContext(dynamicUiType, dynamicUiSubtype, dynamicUiSource);
			// Preserve the user's original choice separately so EditWithRovoBar can
			// detect when the backend overrides it (setDynamicUiContext above is later
			// overwritten with the backend-produced subtype on response).
			setUserSelectedDynamicUiSubtype(dynamicUiSubtype);
		},
		[
			setConversationId,
			config.productKey,
			createConversationOptimistically,
			sendMessage,
			setPrompt,
			setDynamicUiContext,
			setUserSelectedDynamicUiSubtype,
			preferences.storeUserMessages,
			setFinalMessage,
			setContentClassification,
			openRemixPreview,
			onStagingAIFCFlowStarted,
			config.isNodeUnsupportedForRemix,
			experienceTracker,
		],
	);

	return {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		isRemixSafetyViolation,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview: productIntegration.closeRemixPreview,
	};
};
